import { useCallback, useState } from "react";

import { ApiError, isApiError } from "src/core/apiError";
import { EndpointType } from "src/gen/schema/models/endpoints";
import { OperState } from "src/gen/schema/models/global";
import { ConfigD } from "src/utils/api/configD";

import { PollResult, useIntervalPolling } from "./useIntervalPolling";

export interface EndpointCounts {
  email: number;
  amazonS3: number;
  webhooks: number;
  total: number;
}

/**
 * Hook to get the counts of invalid (non-UP) endpoints by type.
 * Polls every 30 seconds to keep counts fresh because there is
 * currently no streaming endpoint for this.
 */
export const useInvalidEndpointSummary = () => {
  const [counts, setCounts] = useState<EndpointCounts>({
    email: 0,
    amazonS3: 0,
    webhooks: 0,
    total: 0
  });
  const [error, setError] = useState<ApiError | undefined>();

  const fetchEndpointCounts = useCallback((): Promise<
    PollResult<EndpointCounts>
  > => {
    return ConfigD.getEndpoints(
      [EndpointType.EMAIL, EndpointType.S3, EndpointType.SPLUNK],
      true
    )
      .then((endpoints) => {
        let emailCount = 0;
        let amazonS3Count = 0;
        let webhooksCount = 0;

        endpoints.forEach((e) => {
          if (e.state === OperState.UP) return;

          // Count invalid endpoints (not UP) by type
          switch (e.type) {
            case EndpointType.EMAIL:
              emailCount++;
              break;
            case EndpointType.S3:
              amazonS3Count++;
              break;
            case EndpointType.SPLUNK:
              webhooksCount++;
              break;
            default:
              break;
          }
        });

        const newCounts = {
          email: emailCount,
          amazonS3: amazonS3Count,
          webhooks: webhooksCount,
          total: emailCount + amazonS3Count + webhooksCount
        };
        setCounts(newCounts);
        setError(undefined);
        // Return data and complete: false to continue polling
        return {
          data: newCounts,
          complete: false
        } as PollResult<EndpointCounts>;
      })
      .catch((err) => {
        const apiError = isApiError(err)
          ? err
          : ApiError.fromMessage("Failed to fetch endpoint counts.");
        setError(apiError);
        // Return data and complete: true to stop polling
        return { complete: false } as PollResult<EndpointCounts>;
      });
  }, []);

  // Set polling with 30 second interval, per discussion with backend
  const [pollingState] = useIntervalPolling(fetchEndpointCounts, {
    intervalTime: 30000,
    startImmediate: true
  });

  return { counts, error, loading: pollingState.isPolling };
};
